import { Button } from '@/components/ui/Button';
import { DEFAULT_CHECKLIST_SPLIT_IMAGE, imageOrDefault } from '../default-images';
import { CtaLink } from '../CtaLink';
import type { SectionRendererProps } from '../section-renderer.types';

interface ChecklistSplitContent {
  eyebrow?: string;
  title: string;
  body?: string[];
  image?: string;
  checklistHeading?: string;
  items: string[];
  ctaLabel?: string;
  ctaUrl?: string;
}

// Investors' diligence-readiness band (MYN-255/MYN-214) has no image and no
// separate checklist panel in Figma - everything (title, body, bullets,
// button) stacks in one white column, unlike Benefits' two-column
// image+colored-panel split below. Keyed the same way every other renderer
// in this folder keys a per-section visual difference.
const SINGLE_COLUMN = new Set(['diligence-readiness']);

/**
 * Checklist split section (FR-124, Benefits' "Be ready for what comes next").
 * Dark-green page background, teal (#00405C) "Are you ready to:" checklist
 * panel - confirmed from Figma's own generated code (`data-name="Are You
 * Ready?"` node uses `#00405c`, not navy) - yellow bullets only (not the item
 * text), per the client's 2026-09-19 design review.
 */
export function ChecklistSplitRenderer({ section }: SectionRendererProps) {
  const c = section.content as unknown as ChecklistSplitContent;
  // Falls back to the default image when the CMS has none set - see default-images.ts.
  const image = imageOrDefault(c.image, DEFAULT_CHECKLIST_SPLIT_IMAGE[section.key]);

  if (SINGLE_COLUMN.has(section.key)) {
    // Background/title/body/items confirmed via Figma's own get_design_context
    // data: slate (--color-slate, #A0AEBC) background, teal title/body/items -
    // not white/text-primary. Eyebrow is dkgreen, NOT Figma's raw navy value:
    // the changes doc explicitly says "use this for the small text at the
    // top: AI DUE DILIGENCE PLATFORM and match the color of the button" - the
    // button is dkgreen (`primary` variant below), and the doc's correction
    // takes priority over Figma's own uncorrected placeholder color.
    return (
      <section className="bg-slate w-full">
        <div className="mx-auto flex w-full max-w-328 flex-col gap-4 px-4 py-16">
          {c.eyebrow && <p className="text-p6 font-semibold text-dkgreen uppercase">{c.eyebrow}</p>}
          <h2 className="text-h2 text-teal">{c.title}</h2>
          {c.body?.map((paragraph, i) => (
            <p key={i} className="text-p4 text-teal">
              {paragraph}
            </p>
          ))}
          <ul className="flex flex-col gap-3">
            {c.items?.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-teal mt-1.5 size-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
                <span className="text-p4 font-medium text-teal">{item}</span>
              </li>
            ))}
          </ul>
          {c.ctaLabel && c.ctaUrl && (
            <Button asChild variant="primary" className="mt-2 w-fit">
              <CtaLink to={c.ctaUrl}>{c.ctaLabel}</CtaLink>
            </Button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="bg-dkgreen w-full">
      <div className="mx-auto grid w-full max-w-328 grid-cols-1 gap-10 px-4 py-16 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          {c.eyebrow && <p className="text-p6 font-semibold text-white uppercase">{c.eyebrow}</p>}
          <h2 className="text-h2 text-white">{c.title}</h2>
          {c.body?.map((paragraph, i) => (
            <p key={i} className="text-p4 text-white/80">
              {paragraph}
            </p>
          ))}
          {image && <img src={image} alt="" className="mt-2 aspect-4/3 w-full rounded-2xl object-cover" />}
        </div>
        <div className="bg-teal flex flex-col gap-4 rounded-2xl p-8">
          {c.checklistHeading && <p className="text-p3 font-semibold text-white">{c.checklistHeading}</p>}
          <ul className="flex flex-col gap-3">
            {c.items?.map((item, i) => (
              <li key={i} className="border-white/10 flex items-start gap-3 border-b pb-3 last:border-0">
                <span className="text-yellow mt-1.5 size-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
                <span className="text-p4 font-medium text-white">{item}</span>
              </li>
            ))}
          </ul>
          {c.ctaLabel && c.ctaUrl && (
            <Button asChild variant="yellow" className="w-fit self-end">
              <CtaLink to={c.ctaUrl}>{c.ctaLabel}</CtaLink>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
